import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListMatchesV1BrandsBrandIdMatchesGetQueryKey,
  useListMatchesV1BrandsBrandIdMatchesGet,
  useMoveListingsV1BrandsBrandIdMatchesMovePost,
  useRenameMatchV1BrandsBrandIdMatchesMatchIdRenamePost,
  useApproveMatchV1BrandsBrandIdMatchesMatchIdApprovePost,
  useBrandSkusV1BrandsSkusGet,
  useSetMatchTrackedV1BrandsBrandIdMatchesMatchIdTrackedPost,
} from "../api/generated/qcsControlPlane";
import type { MatchMember, MatchRow, Platform } from "../api/generated/model";
import { isPlatform, platformKeys, pdpUrl } from "../api/platforms";
import { platformLabel } from "../api/labels";
import { useCanWrite } from "../auth/me";
import { useToast } from "./toast";
import { Badge, Button, EmptyState, ErrorState, Input, Panel, Switch } from "@akaike/ui";
import { Seg } from "./foundation";
import { Freehand } from "./BrandMatchIcons";

/** Which listings the operator has ticked for grouping, as "platform:product_id". */
type Selection = Set<string>;

type CoverageFilter = "all" | "matched" | "unmatched" | "unsure";

/**
 * Below this, the group is worth a human look.
 *
 * Calibrated against the real distribution, and it has to be: the judge is systematically
 * confident, so over the live catalogue the LOWEST verdict on any stored group was 0.84. A
 * bar at 0.6 or 0.8 selects nothing at all and the queue reads "all clear" when nothing has
 * been reviewed. Keep this in step with LOW_CONFIDENCE in the ClickHouse match mixin.
 */
const LOW_CONFIDENCE = 0.95;

/** A group's weakest member confidence - one shaky link makes the whole group shaky. */
const rowConfidence = (row: MatchRow) =>
  Math.min(...(row.members ?? []).map((m) => m.confidence ?? 1), 1);

const memberKey = (m: MatchMember) => `${m.platform}:${m.product_id}`;

const isUnsure = (row: MatchRow) => row.platforms > 1 && rowConfidence(row) < LOW_CONFIDENCE;

/** One icon control. Bordered on purpose: an unbordered 13px glyph in a dim ink reads as
 *  a smudge rather than a button, which is exactly how the first pass landed. */
const ICON_BTN =
  "inline-flex items-center justify-center w-7 h-7 rounded-md border border-line-2 " +
  "text-ink-2 hover:text-ink hover:border-ink-3 hover:bg-panel-hi transition-colors";

/** Longest a name may run in a picker before it crowds out the pack that disambiguates it. */
const OPTION_NAME_MAX = 48;

/**
 * How a match reads in a picker.
 *
 * The canonical name alone is not an identifier: "Alphonso Mango Drink" exists at 200 ml
 * and at 1 L, and the two are indistinguishable by name. The pack is what separates
 * same-named siblings, so it is always kept and the name is what gets truncated.
 */
function matchOptionLabel(row: MatchRow): string {
  const name =
    row.canonical_name.length > OPTION_NAME_MAX
      ? `${row.canonical_name.slice(0, OPTION_NAME_MAX - 1)}…`
      : row.canonical_name;
  return `${name}  ·  ${row.pack_label || "size unknown"}`;
}

/** A platform that does not carry this SKU: the gap, kept visible rather than omitted. */
function GapTile({ platform }: { platform: Platform }) {
  return (
    <div className="rounded border border-dashed border-line p-2 flex flex-col gap-1 opacity-55">
      <span className="text-[10px] uppercase tracking-wide text-ink-3 font-semibold">
        {platformLabel(platform)}
      </span>
      <span className="text-[11.5px] italic text-ink-3">not listed</span>
    </div>
  );
}

/** How wide the popover renders, in px. Needed as a number, not a class, because the
 *  placement below has to know the width before the element is in the document. */
const MOVE_POPOVER_W = 320;

/** Move one listing, from the listing itself. The destination list is the brand's other
 *  groups, so a move can never leave the brand.
 *
 *  Rendered into `document.body` rather than into the tile. The tile sits inside
 *  `MatchCard`, whose `overflow-hidden` keeps its rounded corners from being painted over
 *: and clips this popover along with them, cutting the destination list off mid-row.
 *  Anchoring to the button and portalling out is what lets the list run past the card
 *  edge, which for a card near the bottom of the grid is most of the list. */
function MovePopover({
  row,
  targets,
  pending,
  anchor,
  onMove,
  onClose,
}: {
  row: MatchRow;
  targets: MatchRow[];
  pending: boolean;
  anchor: React.RefObject<HTMLElement | null>;
  onMove: (target: MatchRow | null) => void;
  onClose: () => void;
}) {
  const [target, setTarget] = useState<string>("");
  const [query, setQuery] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  // Sorted by name, not by the grid's order: the grid ranks by coverage so an operator can
  // see the gaps first, which is the wrong order for finding a known SKU by reading. A
  // native <select> could not be searched either, hence the listbox.
  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return targets
      .filter((t) => t.key !== row.key)
      .filter((t) => !needle || matchOptionLabel(t).toLowerCase().includes(needle))
      .sort(
        (a, b) =>
          a.canonical_name.localeCompare(b.canonical_name) ||
          (a.pack_label ?? "").localeCompare(b.pack_label ?? ""),
      );
  }, [targets, row.key, query]);

  // Place it against the button each time the page moves under it. Fixed coordinates
  // have to be recomputed on scroll, and `true` catches scrolls of inner panes too - the
  // grid itself scrolls, so listening on window alone would leave the popover behind.
  useLayoutEffect(() => {
    const place = () => {
      const button = anchor.current?.getBoundingClientRect();
      if (!button) return;
      // Scrolled out of sight, the popover is closed rather than repositioned. Nothing
      // below can place it honestly: with the tile above the fold `button.bottom` goes
      // negative, the flip test fails, and the fallback clamps it to the top of the
      // viewport - a live destination list pinned over whatever card happens to be there,
      // still moving the listing it was opened from. That is the same mis-anchoring the
      // portal was meant to end, and the click-away handler cannot save it because the
      // operator has to notice it is wrong first.
      if (button.bottom < 0 || button.top > window.innerHeight) {
        onClose();
        return;
      }
      const height = box.current?.offsetHeight ?? 0;
      // Right-aligned to the button, then pulled back inside the viewport: the tiles on
      // the far right of a four-platform row would otherwise open off-screen.
      const left = Math.min(
        Math.max(8, button.right - MOVE_POPOVER_W),
        window.innerWidth - MOVE_POPOVER_W - 8,
      );
      const below = button.bottom + 6;
      // Flip above the button when there is no room below, rather than letting the list
      // hang past the fold where the Move button itself becomes unreachable.
      const top =
        below + height > window.innerHeight - 8 && button.top - height - 6 > 8
          ? button.top - height - 6
          : Math.max(8, Math.min(below, window.innerHeight - height - 8));
      setAt({ top, left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor, targets.length, query, onClose]);

  // Click-away and Escape: a popover that only closes via its own button strands the
  // operator when they open one on the wrong tile.
  useEffect(() => {
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t)) return;
      // The trigger toggles on click; closing here as well would make the pair a no-op
      // and the popover would refuse to shut from its own button.
      if (anchor.current?.contains(t)) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={box}
      className="rounded-lg p-2.5 flex flex-col gap-2"
      style={{
        position: "fixed",
        top: at?.top ?? 0,
        left: at?.left ?? 0,
        width: MOVE_POPOVER_W,
        zIndex: 40,
        // Hidden for the first frame only: the flip decision needs a measured height, and
        // a popover that jumps once after opening reads as a glitch.
        visibility: at ? "visible" : "hidden",
        background: "var(--panel)",
        border: "1px solid var(--line-2)",
        boxShadow: "0 10px 30px rgba(0,0,0,.28)",
      }}
      role="dialog"
      aria-label="move this listing"
    >
      <span className="text-[11px] text-ink-3">Move this listing to</span>
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search SKUs…"
        aria-label="search destination SKUs"
        autoFocus
        style={{ height: 30, fontSize: 12 }}
      />
      <div className="pick-list" role="listbox" aria-label="destination SKU">
        <button
          type="button"
          role="option"
          aria-selected={target === ""}
          className={`pick-row${target === "" ? " on" : ""}`}
          onClick={() => setTarget("")}
        >
          a new SKU of its own
        </button>
        <div style={{ maxHeight: 200, overflowY: "auto" }}>
          {options.map((t) => (
            <button
              key={t.key}
              type="button"
              role="option"
              aria-selected={target === t.key}
              className={`pick-row${target === t.key ? " on" : ""}`}
              onClick={() => setTarget(t.key)}
              title={matchOptionLabel(t)}
            >
              {t.canonical_name}
              <span className="sub">
                {t.pack_label || "size unknown"}
                {/* An unmatched destination has no id to join, so picking it makes a NEW
                    SKU out of both listings. Say which kind of thing you are aiming at. */}
                {t.match_id ? ` · ${t.platforms} platforms` : " · unmatched listing"}
              </span>
            </button>
          ))}
          {options.length === 0 && <div className="pick-empty">no SKU matches that</div>}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() => onMove(options.find((t) => t.key === target) ?? null)}
          disabled={pending}
        >
          {pending ? "Moving…" : "Move"}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Everything this brand has on one platform, tickable.
 *
 * Not the grid's rows: the grid only shows what the matcher kept, and the listing an
 * operator most needs to add by hand is usually one it HELD OUT -- the coconut-water
 * six-pack, excluded as a bundle, which therefore appears nowhere on the grid at all. So
 * this reads the SKU dimension directly.
 */
function PlatformPicker({
  platform,
  brand,
  selected,
  onSelect,
}: {
  platform: Platform;
  brand: string;
  selected: Selection;
  onSelect: (key: string) => void;
}) {
  const [query, setQuery] = useState("");
  const skusQ = useBrandSkusV1BrandsSkusGet({
    platform,
    brand,
    limit: 2000,
  });
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const all = skusQ.data ?? [];
    const hit = needle ? all.filter((r) => r.name.toLowerCase().includes(needle)) : all;
    // Ticked first, so a pick never scrolls out of sight when the search changes.
    return [...hit].sort((a, b) => {
      const pa = selected.has(`${platform}:${a.product_id}`) ? 0 : 1;
      const pb = selected.has(`${platform}:${b.product_id}`) ? 0 : 1;
      return pa - pb || a.name.localeCompare(b.name);
    });
  }, [skusQ.data, query, selected, platform]);

  return (
    <div
      className="rounded-lg flex flex-col min-w-0"
      style={{
        border: "1px solid var(--line-2)",
        background: "var(--panel-2)",
      }}
    >
      <div className="flex items-center gap-2 px-2 py-1.5" style={{ borderBottom: "1px solid var(--line-2)" }}>
        <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: `var(--platform-${platform}, inherit)` }}>
          {platformLabel(platform)}
        </span>
        <span className="text-[10px] text-ink-3 ml-auto">
          {skusQ.isLoading ? "…" : `${rows.length}`}
        </span>
      </div>
      <div className="p-1.5">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="filter"
          aria-label={`filter ${platformLabel(platform)} SKUs`}
        />
      </div>
      <div className="flex flex-col gap-0.5 px-1.5 pb-1.5" style={{ maxHeight: 260, overflowY: "auto" }}>
        {rows.length === 0 && !skusQ.isLoading && (
          <span className="text-[11px] text-ink-3 p-1">nothing here</span>
        )}
        {rows.map((sku) => {
          const key = `${platform}:${sku.product_id}`;
          const on = selected.has(key);
          return (
            <label
              key={sku.product_id}
              className="flex items-start gap-1.5 rounded p-1 cursor-pointer text-[11px]"
              style={{ background: on ? "var(--panel-hi)" : undefined }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => onSelect(key)}
                aria-label={`add ${sku.name}`}
              />
              <span className="min-w-0">
                <span className="block leading-tight">{sku.name}</span>
                <span className="text-[10px] text-ink-3">
                  {sku.pack_size || "no pack stated"}
                  {sku.is_bundle && " · bundle"}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Confirm a new SKU before it exists.
 *
 * Grouping used to fire straight off a toolbar button, which is the wrong shape for a
 * write that can quietly dismantle two existing SKUs. The dialog states exactly what is
 * being grouped, which of the picks will LEAVE a group they are already in, and lets the
 * SKU be named at creation instead of renamed afterwards.
 */
function NewGroupModal({
  picks,
  extras,
  leaving,
  pending,
  brand,
  platforms,
  selected,
  onSelect,
  onCreate,
  onClose,
}: {
  picks: { row: MatchRow; member: MatchMember }[];
  extras: string[];
  leaving: number;
  pending: boolean;
  brand: string;
  platforms: Platform[];
  selected: Selection;
  onSelect: (key: string) => void;
  onCreate: (name: string) => void;
  onClose: () => void;
}) {
  // The longest member name is the best default: it is the fullest description present,
  // and the matcher uses the same rule when the model declines to name a group.
  const [name, setName] = useState(
    () => picks.map((p) => p.member.name).sort((a, b) => b.length - a.length)[0] ?? "",
  );
  const [browsing, setBrowsing] = useState(false);
  const total = picks.length + extras.length;
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <Panel
        style={{ width: "100%", maxWidth: browsing ? 1040 : 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Panel.Head title="New SKU group">
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </Panel.Head>
        <Panel.Body>
          <div className="col" style={{ gap: 14 }}>
            <label className="col" style={{ gap: 6 }}>
              <span className="text-[12px] text-ink-2">Name</span>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="what this product is"
                aria-label="SKU name"
                autoFocus
              />
              <span className="text-[11px] text-ink-3">
                Display only - identity is the member set, so renaming never re-points a
                listing.
              </span>
            </label>

            <div className="flex items-center gap-2">
              <Button variant={browsing ? "primary" : "outline"} onClick={() => setBrowsing((v) => !v)}>
                {browsing ? "Hide catalogue" : "Add from catalogue"}
              </Button>
              <span className="text-[11px] text-ink-3">
                Every SKU this brand has on each platform, including the bundles the matcher
                held out - which is where a six-pack lives.
              </span>
            </div>

            {browsing && (
              <div
                className="grid gap-2"
                style={{
                  gridTemplateColumns: `repeat(${platforms.length}, minmax(0, 1fr))`,
                }}
              >
                {platforms.map((p) => (
                  <PlatformPicker
                    key={p}
                    platform={p}
                    brand={brand}
                    selected={selected}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            )}

            {total < 2 && (
              <div
                className="rounded p-2 text-[12px]"
                style={{ background: "var(--panel-2)", color: "var(--warn)" }}
              >
                Pick at least two listings - tick them on the grid behind this dialog, or
                open the catalogue above.
              </div>
            )}

            <div className="col" style={{ gap: 6 }}>
              <span className="text-[12px] text-ink-2">
                {total} listing{total === 1 ? "" : "s"}
                {extras.length > 0 && ` (${extras.length} from the catalogue)`}
              </span>
              <div className="col" style={{ gap: 4, maxHeight: 240, overflowY: "auto" }}>
                {picks.map(({ row, member }) => (
                  <div
                    key={memberKey(member)}
                    className="flex items-baseline gap-2 rounded p-1.5 text-[11.5px]"
                    style={{ background: "var(--panel-2)" }}
                  >
                    <span
                      className="text-[10px] uppercase font-semibold shrink-0"
                      style={{
                        color: `var(--platform-${member.platform}, inherit)`,
                      }}
                    >
                      {platformLabel(member.platform)}
                    </span>
                    <span className="min-w-0 grow truncate" title={member.name}>
                      {member.name}
                    </span>
                    <span className="font-mono text-[10px] text-ink-3 shrink-0">
                      {member.product_id}
                    </span>
                    {/* The consequence, named per listing rather than as a total. */}
                    {row.match_id && (
                      <Badge variant="outline-warning" style={{ fontSize: 9 }} title={row.canonical_name}>
                        leaves {row.canonical_name.slice(0, 24)}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {leaving > 0 && (
              <span className="text-[11.5px]" style={{ color: "var(--warn)" }}>
                {leaving} of these already belong to a SKU and will be moved out of it. A
                listing belongs to exactly one group.
              </span>
            )}

            <div className="flex gap-2">
              <Button onClick={() => onCreate(name.trim())} disabled={pending || total < 2}>
                {pending ? "Creating…" : `Create SKU from ${total} listings`}
              </Button>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        </Panel.Body>
      </Panel>
    </div>
  );
}

/** Everything one platform records about this product, which is the point of the view:
 *  the same jar is described differently on each, and the differences are the data. */
function ListingTile({
  member,
  row,
  canWrite,
  targets,
  movePending,
  onMove,
  selectable,
  selected,
  onSelect,
}: {
  member: MatchMember;
  row: MatchRow;
  canWrite: boolean;
  targets: MatchRow[];
  movePending: boolean;
  onMove: (member: MatchMember, target: MatchRow | null) => void;
  selectable: boolean;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const [moving, setMoving] = useState(false);
  // The popover renders in a portal, so it needs the button it belongs to: both to place
  // itself against and to tell its own trigger apart from a click-away.
  const moveButton = useRef<HTMLButtonElement>(null);
  const leaf = (member.category_path ?? []).slice(-1)[0] ?? member.product_type ?? "";
  const inactive = member.status !== "" && member.status !== "active";
  return (
    <div
      className="relative rounded-lg p-2.5 flex flex-col gap-2 min-w-0"
      style={{
        // Its own surface AND a real border. --line-strong/--surface-* were invented
        // names that resolve to nothing, which is why the tiles ran together with no
        // visible edge at all; tokens.css defines --line/--line-2/--panel*.
        border: "1px solid var(--line-2)",
        borderTop: `3px solid var(--platform-${member.platform}, var(--line-2))`,
        background: "var(--panel-2)",
        boxShadow: "0 1px 2px rgba(0,0,0,.06)",
      }}
    >
      <div className="flex items-center gap-1.5">
        {/* Selectable only in grouping mode. A listing already in a SKU can be picked
            too - that is a split, and it LEAVES its old group rather than joining two. */}
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(memberKey(member))}
            aria-label={`select ${member.platform} ${member.product_id} for grouping`}
          />
        )}
        <span
          className="text-[10px] uppercase tracking-wide font-semibold"
          style={{ color: `var(--platform-${member.platform}, inherit)` }}
        >
          {platformLabel(member.platform)}
        </span>
        {/* Delisted is the reason a platform shows a gap tomorrow - say it here. A dot
            plus a word, because a bare dot needs a legend and a bare word needs reading. */}
        <span
          className="inline-flex items-center gap-1 text-[10px]"
          style={{ color: inactive ? "var(--warn)" : "var(--ok)" }}
          title={`this listing is ${member.status || "inactive"} on ${platformLabel(member.platform)}`}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: "currentColor" }}
            aria-hidden
          />
          {inactive ? member.status || "inactive" : "active"}
        </span>
        {/* The per-LISTING tracked switch is hidden for now: tracking is decided per
            matched product (the switch in the card header), and two controls for one
            intent is how they drift apart. Nothing is removed -- the field, the endpoint
            and the Brand page's own per-SKU column all still work, so one listing can
            still be retired there. It surfaces here only when it DISAGREES with its group,
            which is a state the group switch cannot explain by itself. */}
        {member.tracked === false && (
          <Badge
            variant="outline-warning"
            style={{ fontSize: 9.5 }}
            title="this listing is switched off - excluded from cart inventory and store-SKU map runs"
          >
            untracked
          </Badge>
        )}
        {member.method === "override" && (
          <Badge variant="outline-info" style={{ fontSize: 9.5 }} title="pinned by an operator">
            pinned
          </Badge>
        )}

        <span className="ml-auto flex items-center gap-1">
          <Link className={ICON_BTN} to={`/skus/${member.platform}/${member.product_id}`} title="open this SKU in qcom_scrape">
            <Freehand name="inspect" size={16} />
            <span className="sr-only">open in qcom_scrape</span>
          </Link>
          <a
            className={ICON_BTN}
            href={pdpUrl(member.platform, member.product_id)}
            target="_blank"
            rel="noreferrer"
            title={`open on ${platformLabel(member.platform)}`}
          >
            <Freehand name="open-external" size={16} />
            <span className="sr-only">open on {platformLabel(member.platform)}</span>
          </a>
          {canWrite && <button
            ref={moveButton}
            type="button"
            className={ICON_BTN}
            onClick={() => setMoving((v) => !v)}
            title="move this listing to another SKU"
            aria-expanded={moving}
          >
            <Freehand name="move" size={16} />
            <span className="sr-only">move this listing</span>
          </button>}
        </span>
      </div>

      {canWrite && moving && (
        <MovePopover
          row={row}
          targets={targets}
          pending={movePending}
          anchor={moveButton}
          onMove={(target) => {
            onMove(member, target);
            setMoving(false);
          }}
          onClose={() => setMoving(false)}
        />
      )}

      <div className="flex gap-2 min-w-0">
        {member.image_url ? (
          <img
            src={member.image_url}
            alt=""
            loading="lazy"
            className="w-11 h-11 rounded object-cover bg-panel-hi shrink-0"
            style={{ opacity: inactive ? 0.5 : 1 }}
          />
        ) : (
          <div className="w-11 h-11 rounded bg-panel-hi shrink-0" aria-hidden />
        )}
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="text-[12px] leading-tight">{member.name}</span>
          <span className="text-[10.5px] text-ink-3">
            {member.pack_size || "no pack stated"}
            {member.display_brand && ` · ${member.display_brand}`}
          </span>
        </div>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[10px] text-ink-3 min-w-0">
        <dt>product</dt>
        <dd className="font-mono break-all text-ink-2">{member.product_id}</dd>
        {member.variant_id && member.variant_id !== member.product_id && (
          <>
            <dt>variant</dt>
            <dd className="font-mono break-all">{member.variant_id}</dd>
          </>
        )}
        {member.sku && member.sku !== member.product_id && (
          <>
            <dt>sku</dt>
            <dd className="font-mono break-all">{member.sku}</dd>
          </>
        )}
        {leaf && (
          <>
            <dt>category</dt>
            <dd className="truncate" title={(member.category_path ?? []).join(" > ")}>
              {leaf}
            </dd>
          </>
        )}
        {member.manufacturer && (
          <>
            <dt>maker</dt>
            <dd className="truncate" title={member.manufacturer}>
              {member.manufacturer}
            </dd>
          </>
        )}
        {member.last_seen_at && (
          <>
            <dt>seen</dt>
            <dd>{member.last_seen_at.slice(0, 10)}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

/**
 * The brand's cross-platform SKU grid, and the one edit that maintains it.
 *
 * Editing is a single operation - move a listing to a match - because split, merge and
 * correction are all that operation with a different target. It lives on the listing
 * itself: the thing being moved is the thing you click.
 */
export function BrandMatchGrid({ brandId }: { brandId: string }) {
  const canWrite = useCanWrite();
  const notify = useToast();
  const qc = useQueryClient();
  // The filter is in the URL so the brand list can link straight to the review queue, and
  // so a filtered view survives a reload or being pasted to someone else.
  const [params, setParams] = useSearchParams();
  const coverage: CoverageFilter =
    params.get("review") === "low"
      ? "unsure"
      : params.get("coverage") === "matched"
        ? "matched"
        : params.get("coverage") === "unmatched"
          ? "unmatched"
          : "all";
  const setCoverage = (next: CoverageFilter) => {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === "unsure") out.set("review", "low");
        else out.delete("review");
        if (next === "matched" || next === "unmatched") out.set("coverage", next);
        else out.delete("coverage");
        return out;
      },
      { replace: true },
    );
  };
  const [gapOn, setGapOn] = useState<string>("");
  const [query, setQuery] = useState("");
  const showCombos = params.get("view") === "combos";
  const setShowCombos = (on: boolean) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (on) out.set("view", "combos");
        else out.delete("view");
        return out;
      },
      { replace: true },
    );
  const [grouping, setGrouping] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selected, setSelected] = useState<Selection>(new Set());
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameText, setRenameText] = useState("");

  const matchesQ = useListMatchesV1BrandsBrandIdMatchesGet(brandId);
  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: getListMatchesV1BrandsBrandIdMatchesGetQueryKey(brandId),
    });

  const move = useMoveListingsV1BrandsBrandIdMatchesMovePost({
    mutation: {
      onSuccess: (r) => {
        notify(r.created ? "Moved into a new SKU" : "Moved", "ok");
        void invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "move failed", "crit"),
    },
  });

  const rename = useRenameMatchV1BrandsBrandIdMatchesMatchIdRenamePost({
    mutation: {
      onSuccess: () => {
        notify("Renamed", "ok");
        setRenaming(null);
        void invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "rename failed", "crit"),
    },
  });

  const approve = useApproveMatchV1BrandsBrandIdMatchesMatchIdApprovePost({
    mutation: {
      onSuccess: () => {
        notify("Approved - this SKU is now operator-owned", "ok");
        void invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "approve failed", "crit"),
    },
  });

  const setGroupTracked = useSetMatchTrackedV1BrandsBrandIdMatchesMatchIdTrackedPost({
    mutation: {
      onSuccess: () => {
        notify("Tracking updated for every listing in this SKU", "ok");
        void invalidate();
      },
      onError: (e) => notify(e instanceof Error ? e.message : "could not change tracking", "crit"),
    },
  });

  const platforms = platformKeys();
  // Memoised: a fresh [] each render would re-run the filter below on every keystroke.
  const rows = useMemo(() => matchesQ.data?.rows ?? [], [matchesQ.data]);
  // Combos are a separate view, never part of the counts below: they are not SKUs this
  // brand sells once, so folding them into "unmatched" would inflate the work queue with
  // rows nobody should be matching.
  const grid = useMemo(() => rows.filter((r) => !r.is_combo), [rows]);
  const comboRows = useMemo(() => rows.filter((r) => r.is_combo), [rows]);
  const unsureCount = useMemo(() => grid.filter(isUnsure).length, [grid]);
  const unmatchedCount = useMemo(() => grid.filter((r) => r.platforms === 1).length, [grid]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (showCombos ? comboRows : grid).filter((row) => {
      if (showCombos) return !needle || row.canonical_name.toLowerCase().includes(needle);
      if (coverage === "matched" && row.platforms < 2) return false;
      if (coverage === "unmatched" && row.platforms !== 1) return false;
      if (coverage === "unsure" && !isUnsure(row)) return false;
      if (gapOn && (row.members ?? []).some((m) => m.platform === gapOn)) return false;
      if (needle && !row.canonical_name.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [grid, comboRows, showCombos, coverage, gapOn, query]);

  const stopGrouping = () => {
    setGrouping(false);
    setConfirming(false);
    setSelected(new Set());
  };

  const toggleSelected = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // Grouping is a move to a null target - the same write the split path uses, which is why
  // there is no "create group" endpoint to keep in step with anything.
  const groupSelected = (name: string) =>
    move.mutate(
      {
        brandId,
        data: {
          // Keys are built from Platform values, so the guard only narrows the type.
          listings: [...selected].flatMap((key) => {
            const [platform, ...rest] = key.split(":");
            return isPlatform(platform) ? [{ platform, product_id: rest.join(":") }] : [];
          }),
          target: null,
          reason: "operator grouped listings into a new SKU",
        },
      },
      {
        onSuccess: (created) => {
          // The move endpoint names the new group after its first listing; a name typed
          // here replaces that. Second call rather than a widened request body, so the
          // create path stays the one write every other edit already uses.
          if (name && created.match_id) {
            rename.mutate({
              brandId,
              matchId: created.match_id,
              data: { canonical_name: name },
            });
          }
          stopGrouping();
        },
      },
    );

  // A listing belongs to exactly one SKU, so picking one that is already grouped moves it
  // OUT of that group. Carry the row alongside the member so the dialog can name what each
  // pick is about to leave, rather than reporting a bare count.
  const picks = useMemo(
    () =>
      rows.flatMap((row) =>
        (row.members ?? [])
          .filter((member) => selected.has(memberKey(member)))
          .map((member) => ({ row, member })),
      ),
    [rows, selected],
  );
  const leaving = picks.filter((p) => p.row.match_id).length;
  // Ticked in the catalogue but absent from the grid: a bundle the matcher held out, or a
  // listing on a filter that is not showing. They are still part of the group.
  const extras = useMemo(() => {
    const onGrid = new Set(picks.map((p) => memberKey(p.member)));
    return [...selected].filter((key) => !onGrid.has(key));
  }, [picks, selected]);

  const moveListing = (member: MatchMember, target: MatchRow | null) => {
    const ref = (m: MatchMember) => ({
      platform: m.platform,
      product_id: m.product_id,
    });
    // Onto a GROUP: the listing joins it. Onto an UNMATCHED listing: there is no id to
    // join, so both listings move to a null target and become one new SKU together.
    const listings = target?.match_id
      ? [ref(member)]
      : [ref(member), ...(target?.members ?? []).map(ref)];
    move.mutate({
      brandId,
      data: {
        listings,
        target: target?.match_id ?? null,
        reason: "operator correction",
      },
    });
  };

  // Every row is a destination, groups AND unmatched listings. Restricting this to groups
  // made the commonest correction impossible: pairing two listings the matcher left apart,
  // which is precisely what an operator opens this view to do.
  const targets = rows;

  if (matchesQ.isError) {
    return (
      <ErrorState
        title="Could not load matches"
        message={matchesQ.error instanceof Error ? matchesQ.error.message : undefined}
        onRetry={() => void matchesQ.refetch()}
      />
    );
  }

  return (
    <Panel>
      <Panel.Body style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Above the filters, because it switches WHICH set is being filtered rather than
            narrowing the current one. */}
        <div className="flex items-center gap-2 flex-wrap">
          <Switch
            checked={showCombos}
            onCheckedChange={setShowCombos}
            aria-label="show held-out combos"
          />
          <span className="text-[12px] text-ink-2">
            Combos held out{" "}
            <b className="text-ink">{matchesQ.data?.combos_excluded ?? comboRows.length}</b>
          </span>
          <span className="text-[11px] text-ink-3">
            {showCombos
              ? "Showing the bundles the matcher excluded - multipacks and mixed bundles. Group one by hand if it should be a SKU."
              : "Multipacks and mixed bundles, kept out of the grid. Turn on to inspect them."}
          </span>
        </div>

        <div
          className="flex flex-wrap gap-3 items-center"
          style={{
            opacity: showCombos ? 0.4 : 1,
            pointerEvents: showCombos ? "none" : undefined,
          }}
        >
          <Seg
            value={coverage}
            onChange={(v) => setCoverage(v)}
            options={[
              { value: "all", label: "All SKUs" },
              { value: "matched", label: "Matched" },
              { value: "unmatched", label: `Unmatched (${unmatchedCount})` },
              // The review queue, and the count is the reason to click it.
              { value: "unsure", label: `Low confidence (${unsureCount})` },
            ]}
          />
          <label className="text-[12px] text-ink-2 flex items-center gap-2">
            Gap on
            <select
              className="select"
              value={gapOn}
              onChange={(e) => setGapOn(e.target.value)}
              aria-label="show only SKUs missing from a platform"
            >
              <option value="">any platform</option>
              {platforms.map((p) => (
                <option key={p} value={p}>
                  {platformLabel(p)}
                </option>
              ))}
            </select>
          </label>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a product"
            aria-label="find a product"
            style={{ maxWidth: 220 }}
          />
          {/* Always on screen: grouping was only discoverable by noticing a checkbox. */}
          {canWrite && <Button
            variant={grouping ? "primary" : "outline"}
            onClick={() => {
              if (grouping) stopGrouping();
              else {
                setGrouping(true);
                // Straight to the dialog: it explains the tick-then-create flow, which a
                // bare checkbox column does not.
                setConfirming(true);
              }
            }}
            title="pick listings and put them in one SKU"
          >
            {grouping ? "Cancel grouping" : "New group"}
          </Button>}
          <span className="text-[12px] text-ink-3 ml-auto">
            {matchesQ.data ? (
              <>
                {showCombos ? (
                  <>
                    <b className="text-ink">{comboRows.length}</b> held-out combos · showing{" "}
                    {visible.length}
                  </>
                ) : (
                  <>
                    <b className="text-ink">{matchesQ.data.matched}</b> matched of{" "}
                    <b className="text-ink">{matchesQ.data.total}</b> · showing {visible.length}
                  </>
                )}
              </>
            ) : (
              "loading…"
            )}
          </span>
        </div>

        {canWrite && grouping && (
          <div
            className="flex flex-wrap gap-3 items-center rounded-lg p-2.5"
            style={{
              background: "var(--panel-2)",
              border: "1px solid var(--line-2)",
            }}
          >
            <Badge variant="outline-info">{selected.size} selected</Badge>
            <Button onClick={() => setConfirming(true)}>Create group…</Button>
            <Button variant="outline" onClick={stopGrouping}>
              Cancel
            </Button>
            <span className="text-[11px] text-ink-3">
              {selected.size < 2
                ? "Tick two or more listings, on any card."
                : leaving > 0
                  ? `${leaving} of them will LEAVE their current SKU - a listing belongs to exactly one.`
                  : "These will become one SKU."}
            </span>
          </div>
        )}

        {canWrite && confirming && (
          <NewGroupModal
            picks={picks}
            extras={extras}
            brand={matchesQ.data?.brand ?? ""}
            platforms={platforms}
            selected={selected}
            onSelect={toggleSelected}
            leaving={leaving}
            pending={move.isPending || rename.isPending}
            onCreate={groupSelected}
            onClose={() => setConfirming(false)}
          />
        )}

        {visible.length === 0 ? (
          <EmptyState title={showCombos ? "No combos held out" : "No SKUs match these filters"}>
            {showCombos
              ? "Nothing was excluded as a bundle for this brand - or the job ran with Exclude combos turned off."
              : rows.length === 0
                ? "This brand has not been matched yet. Run the Product match flow to group its listings across platforms."
                : "Widen the coverage filter or clear the search."}
          </EmptyState>
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((row) => (
              <MatchCard
                key={row.key}
                row={row}
                canWrite={canWrite}
                platforms={platforms}
                targets={targets}
                movePending={move.isPending}
                onMove={moveListing}
                approvePending={approve.isPending}
                onApprove={() =>
                  row.match_id && approve.mutate({ brandId, matchId: row.match_id })
                }
                onSetGroupTracked={(on) =>
                  row.match_id &&
                  setGroupTracked.mutate({
                    brandId,
                    matchId: row.match_id,
                    params: { tracked: on },
                  })
                }
                selectable={grouping}
                selected={selected}
                onSelect={toggleSelected}
                renaming={renaming === row.key}
                renameText={renameText}
                onRenameStart={() => {
                  setRenaming(row.key);
                  setRenameText(row.canonical_name);
                }}
                onRenameText={setRenameText}
                onRenameCancel={() => setRenaming(null)}
                onRenameSave={() =>
                  row.match_id &&
                  rename.mutate({
                    brandId,
                    matchId: row.match_id,
                    data: { canonical_name: renameText },
                  })
                }
              />
            ))}
          </div>
        )}
      </Panel.Body>
    </Panel>
  );
}

function MatchCard({
  row,
  canWrite,
  platforms,
  targets,
  movePending,
  onMove,
  approvePending,
  onApprove,
  onSetGroupTracked,
  selectable,
  selected,
  onSelect,
  renaming,
  renameText,
  onRenameStart,
  onRenameText,
  onRenameCancel,
  onRenameSave,
}: {
  row: MatchRow;
  canWrite: boolean;
  platforms: Platform[];
  targets: MatchRow[];
  movePending: boolean;
  onMove: (member: MatchMember, target: MatchRow | null) => void;
  approvePending: boolean;
  onApprove: () => void;
  onSetGroupTracked: (tracked: boolean) => void;
  selectable: boolean;
  selected: Selection;
  onSelect: (key: string) => void;
  renaming: boolean;
  renameText: string;
  onRenameStart: () => void;
  onRenameText: (value: string) => void;
  onRenameCancel: () => void;
  onRenameSave: () => void;
}) {
  const byPlatform = new Map((row.members ?? []).map((m) => [m.platform, m]));
  const carried = platforms.filter((p) => byPlatform.has(p));
  const unsure = isUnsure(row);
  return (
    <section
      className="rounded-lg panel-inset overflow-hidden"
      style={{
        border: `1px solid ${unsure ? "var(--warn)" : "var(--line-2)"}`,
      }}
    >
      <header
        className="flex flex-wrap gap-3 items-center px-3 py-2"
        style={{ borderBottom: "1px solid var(--line-2)" }}
      >
        {renaming ? (
          <div className="flex gap-2 items-center grow">
            <Input
              value={renameText}
              onChange={(e) => onRenameText(e.target.value)}
              aria-label="SKU name"
              autoFocus
            />
            <Button onClick={onRenameSave}>Save</Button>
            <Button variant="outline" onClick={onRenameCancel}>
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <span className="text-[13px] font-semibold min-w-0">{row.canonical_name}</span>
            {/* An explicit control, not a click target hidden in the title: the name is
                read far more often than it is edited. */}
            {row.match_id ? (
              canWrite && (
                <Button variant="outline" size="sm" onClick={onRenameStart} title="edit this SKU name">
                  <Freehand name="edit" size={14} />
                  Edit
                </Button>
              )
            ) : (
              // No identity row exists for a lone listing, so there is nothing to rename:
              // the heading IS the platform's own listing name. Say that, rather than
              // leaving a gap where every other card has a button.
              <span
                className="text-[10px] text-ink-3"
                title="This is one platform's listing, not a matched SKU. Its name comes from the platform - group it with another listing and the SKU becomes nameable."
              >
                unmatched · name from the platform
              </span>
            )}
          </>
        )}
        <span className="text-[11px] text-ink-3">{row.pack_label || "size unknown"}</span>
        {/* WHY, not just how sure. A confidence number alone gives an operator nothing to
            check; the judge's own one-liner is what makes a 0.92 reviewable. */}
        {row.reason && (
          <span className="text-[11px] italic text-ink-2" title={row.reason}>
            “{row.reason}”
          </span>
        )}
        {unsure && (
          <Badge
            variant="outline-warning"
            style={{
              fontSize: 9.5,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            title={`weakest link in this group scored ${rowConfidence(row).toFixed(2)}`}
          >
            <Freehand name="warning" size={12} />
            low confidence
          </Badge>
        )}
        {/* The queue needs an exit. Approving pins the members at confidence 1.0, so the
            group leaves the low-confidence filter AND the next run reproduces it. */}
        {canWrite && unsure && row.match_id && (
          <Button variant="outline" size="sm" onClick={onApprove} disabled={approvePending}>
            {approvePending ? "Approving…" : "Approve"}
          </Button>
        )}
        {/* Coverage as filled/hollow platform dots: the whole point of a row, readable
            without counting tiles. */}
        <span className="flex gap-1 items-center ml-auto">
          {platforms.map((p) => (
            <span
              key={p}
              title={`${platformLabel(p)}: ${byPlatform.has(p) ? "listed" : "not listed"}`}
              className="w-2 h-2 rounded-full"
              style={{
                background: byPlatform.has(p) ? `var(--platform-${p}, var(--ink-3))` : "transparent",
                boxShadow: byPlatform.has(p) ? "none" : "inset 0 0 0 1px var(--line-2)",
              }}
            />
          ))}
          <span className="text-[11px] text-ink-3 ml-1">
            {carried.length} of {platforms.length}
          </span>
          {row.manual && (
            <Badge variant="outline-info" style={{ fontSize: 9.5 }} title="an operator edited this group">
              edited
            </Badge>
          )}
          {/* One switch for the whole product. It writes the per-SKU override for every
              member, which is what cart inventory and store-SKU map actually read. */}
          {row.match_id && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-ink-3 ml-2"
              title={
                row.tracked === false
                  ? "untracked - every listing in this SKU is excluded from scrape runs"
                  : "tracked - every listing in this SKU is scraped"
              }
            >
              {canWrite && (
                <Switch
                  checked={row.tracked !== false}
                  onCheckedChange={onSetGroupTracked}
                  aria-label={`track ${row.canonical_name}`}
                />
              )}
              {row.tracked === false ? "untracked" : "tracked"}
            </span>
          )}
        </span>
      </header>
      <div
        className="grid gap-3 p-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}
      >
        {platforms.map((p) => {
          const member = byPlatform.get(p);
          return member ? (
            <ListingTile
              key={p}
              member={member}
              row={row}
              canWrite={canWrite}
              targets={targets}
              movePending={movePending}
              onMove={onMove}
              selectable={canWrite && selectable}
              selected={selected.has(memberKey(member))}
              onSelect={onSelect}
            />
          ) : (
            <GapTile key={p} platform={p} />
          );
        })}
      </div>
    </section>
  );
}
